import React from "react";
import { compose } from "recompose";
import { connect } from "react-redux";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/styles";
import { Field, reduxForm, getFormValues } from "redux-form";
import { PhoneOutlined, EmailOutlined, PersonOutline } from "@material-ui/icons";
import { validate as validateEmail } from "isemail";
import { useTracker } from "@dpdgroupuk/react-event-tracker";
import SubmitButton from "../SubmitButton";
import FormField from "../FormField";
import { InputFormField } from "../Input/index";
import { isPhoneValid } from "../../utils/validation";
import { CONTACT_NAME_REQUIRED, INVALID_EMAIL, INVALID_PHONE } from "../../constants/validation.js";
const useStyles = makeStyles(({
  breakpoints,
  palette,
  typography
}) => ({
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [breakpoints.down("sm")]: {
      padding: `0 ${typography.pxToRem(40)}`
    }
  },
  title: {
    [breakpoints.down("xs")]: {
      fontSize: typography.pxToRem(15),
      lineHeight: 1.72,
      fontWeight: 300
    },
    [breakpoints.up("sm")]: {
      fontSize: typography.pxToRem(21),
      lineHeight: 1.49
    },
    letterSpacing: 0.44,
    textAlign: "center",
    margin: `${typography.pxToRem(40)} 0`,
    maxWidth: typography.pxToRem(500)
  },
  icon: {
    fill: palette.primary.main,
    verticalAlign: "middle",
    marginRight: typography.pxToRem(10),
    alignSelf: "center",
    [breakpoints.down("xs")]: {
      fontSize: typography.pxToRem(16)
    },
    [breakpoints.up("sm")]: {
      fontSize: typography.pxToRem(24)
    }
  },
  inputRow: {
    width: "100%",
    maxWidth: typography.pxToRem(510),
    marginBottom: typography.pxToRem(30),
    alignItems: "center"
  },
  label: {
    fontWeight: 300,
    [breakpoints.down("xs")]: {
      fontSize: typography.pxToRem(16),
      marginBottom: typography.pxToRem(8)
    },
    [breakpoints.up("sm")]: {
      fontSize: typography.pxToRem(19),
      marginBottom: 0
    }
  },
  input: {
    [breakpoints.up("sm")]: {
      marginLeft: typography.pxToRem(20)
    }
  }
}));

const validate = values => {
  const errors = {};

  if (!values.contactName) {
    errors.contactName = CONTACT_NAME_REQUIRED;
  }

  if (values.contactEmail && !validateEmail(values.contactEmail)) {
    errors.contactEmail = INVALID_EMAIL;
  }

  if (!isPhoneValid(values.contactPhone)) {
    errors.contactPhone = INVALID_PHONE;
  }

  return errors;
};

const ContactDetailsForm = ({
  values = {},
  title,
  valid,
  handleSubmit,
  pristine,
  analytics = {}
}) => {
  const tracker = useTracker();
  const styles = useStyles();
  const fieldStyles = {
    label: styles.label,
    inputRow: styles.inputRow
  };
  const gridOptions = {
    flex: {
      direction: "row",
      justify: "space-between"
    },
    placeholder: {
      xs: 12,
      sm: 4
    },
    input: {
      xs: 12,
      sm: 8
    }
  };
  const isPhoneOrEmailEntered = values.contactPhone || values.contactEmail;
  const isSubmitDisabled = pristine || !valid || !isPhoneOrEmailEntered;
  const handleTrackableSubmit = React.useCallback((...args) => {
    tracker.logEvent(analytics.ON_SUBMIT);
    return handleSubmit(...args);
  }, [analytics.ON_SUBMIT, handleSubmit, tracker]);
  const onContactName = React.useCallback(() => {
    tracker.logEvent(analytics.ON_CONTACT);
  }, [analytics.ON_CONTACT, tracker]);
  const onPhone = React.useCallback(() => {
    tracker.logEvent(analytics.ON_PHONE);
  }, [analytics.ON_PHONE, tracker]);
  const onEmail = React.useCallback(() => {
    tracker.logEvent(analytics.ON_EMAIL);
  }, [analytics.ON_EMAIL, tracker]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement(Typography, {
    className: styles.title
  }, title), /*#__PURE__*/React.createElement(FormField, {
    placeholder: "Contact Name",
    iconComponent: /*#__PURE__*/React.createElement(PersonOutline, {
      className: styles.icon
    }),
    styles: fieldStyles,
    gridOptions: gridOptions
  }, /*#__PURE__*/React.createElement(Field, {
    className: styles.input,
    component: InputFormField,
    name: "contactName",
    fullWidth: true,
    onBlur: onContactName
  })), /*#__PURE__*/React.createElement(FormField, {
    placeholder: "Mobile Number",
    iconComponent: /*#__PURE__*/React.createElement(PhoneOutlined, {
      className: styles.icon
    }),
    styles: fieldStyles,
    gridOptions: gridOptions
  }, /*#__PURE__*/React.createElement(Field, {
    className: styles.input,
    component: InputFormField,
    name: "contactPhone",
    fullWidth: true,
    onBlur: onPhone
  })), /*#__PURE__*/React.createElement(FormField, {
    placeholder: "Email Address",
    iconComponent: /*#__PURE__*/React.createElement(EmailOutlined, {
      className: styles.icon
    }),
    styles: fieldStyles,
    gridOptions: gridOptions
  }, /*#__PURE__*/React.createElement(Field, {
    className: styles.input,
    component: InputFormField,
    name: "contactEmail",
    fullWidth: true,
    onBlur: onEmail
  })), /*#__PURE__*/React.createElement(SubmitButton, {
    onSubmit: handleTrackableSubmit,
    isSubmitDisabled: isSubmitDisabled
  }));
};

export default compose(reduxForm({
  form: "getContacts",
  validate
}), connect(state => ({
  values: getFormValues("getContacts")(state)
})))(ContactDetailsForm);